/**
 * Shared brand-aligned colors for log views and status affordances.
 * Prefer CSS custom properties from @veilstream/ui/tokens.css.
 *
 * Success (olive) = passed / ready / affirmative.
 * Info / sky = running / live / in-progress.
 */

/** Distinct container accents — semantic tokens so they stay readable in light and dark. */
export const CONTAINER_COLORS = [
  'var(--vs-info)',
  'var(--vs-accent)',
  'var(--vs-warning)',
  'var(--vs-link)',
  'var(--vs-success)',
  'var(--vs-error-soft)',
  'var(--palette-primary-500)',
  'var(--vs-pending)',
  'var(--palette-primary-400)',
  'var(--palette-secondary1-400)',
] as const

/** Log message body — never a hardcoded light gray. */
export const LOG_MESSAGE_COLOR = 'var(--vs-text-primary)'
export const LOG_TIMESTAMP_COLOR = 'var(--vs-text-muted)'

export function containerColor(name: string): string {
  let h = 0
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0
  return CONTAINER_COLORS[h % CONTAINER_COLORS.length]
}

/** Map log/runtime level strings to semantic token colors. */
export function levelColor(level?: string): string {
  switch (level?.toUpperCase()) {
    case 'ERROR':
      return 'var(--vs-log-error)'
    case 'WARN':
    case 'WARNING':
      return 'var(--vs-log-warning)'
    case 'DEBUG':
      return 'var(--vs-log-debug)'
    case 'EVENT':
      return 'var(--vs-log-event)'
    default:
      return 'var(--vs-log-info)'
  }
}

export function qaStatusColor(status: string): string {
  if (status === 'passed') return 'var(--vs-success)'
  if (status === 'failed') return 'var(--vs-error)'
  if (status === 'running') return 'var(--vs-info)'
  if (status === 'skipped' || status === 'no_config') return 'var(--vs-text-muted)'
  return 'var(--vs-pending)'
}

/** Text color for system-event log lines (log source === 'system'). */
export const SYSTEM_LOG_COLOR = 'var(--vs-log-event)'

/** Row treatment for system-event log lines — marker border + faint tint. */
export const SYSTEM_LOG_LINE_SX = {
  borderLeft: `3px solid ${SYSTEM_LOG_COLOR}`,
  paddingLeft: '6px',
  backgroundColor: 'color-mix(in srgb, var(--vs-log-event) 8%, transparent)',
} as const

export const LIVE_CHIP_SX = {
  backgroundColor: 'var(--palette-primary-600)',
  color: 'var(--vs-on-action)',
  fontWeight: 700,
} as const

/** Assumed-role banner — deep error red from brand ramp */
export const ROLE_ASSUMED_BG = 'var(--palette-warning-600)'
export const ROLE_ASSUMED_BORDER = 'var(--palette-warning-700)'
